import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Select,
} from "@chakra-ui/react";
import { useCallback } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Quiz } from "../../models/Quiz";
import useCreateOrEditQuiz from "../../hooks/api/quiz/useCreateOrEditQuiz";

type Props = {
  quiz?: Quiz;
  lessonId?: string;
  onSuccess?: (lessonId: number) => void;
  onError?: () => void;
};

export default function QuizForm({
  quiz,
  lessonId,
  onSuccess,
  onError,
}: Props) {
  const { t } = useTranslation();
  const {
    handleSubmit,
    register,
    formState: { errors, isValid, isSubmitted },
  } = useForm<Quiz>({
    defaultValues: {
      title: quiz?.title || "",
      description: quiz?.description || "",
      lessonId: lessonId,
    },
  });

  const { mutateAsync: createOrEditQuiz, isPending } = useCreateOrEditQuiz(
    lessonId ?? "",
  );

  const onSubmit = useCallback(
    (values: Quiz) => {
      createOrEditQuiz({
        quiz: { id: quiz?.id, ...values },
        operation: quiz ? "edit" : "create",
      })
        .then((response) => onSuccess?.(response.id))
        .catch(onError);
    },
    [createOrEditQuiz, quiz, onError, onSuccess],
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FormControl
        isInvalid={!isValid && isSubmitted}
        display="flex"
        flexDirection="column"
        gap={4}
      >
        <div>
          <FormLabel htmlFor="name">{t("forms.name")}</FormLabel>
          <Input
            id="title"
            placeholder={t("forms.title")}
            {...register("title", { required: t("forms.validation.required") })}
          />
          <FormErrorMessage>
            {errors.title && errors.title.message}
          </FormErrorMessage>
        </div>
        <div>
          <FormLabel htmlFor="description">{t("forms.description")}</FormLabel>
          <Input
            id="description"
            placeholder={t("forms.description")}
            {...register("description", {
              required: t("forms.validation.required"),
            })}
          />
          <FormErrorMessage>
            {errors.description && errors.description.message}
          </FormErrorMessage>
        </div>
        <div>
          <FormLabel htmlFor="type">{t("forms.type")}</FormLabel>
          <Select
            id="type"
            {...register("type", {
              required: t("forms.validation.required"),
            })}
            defaultValue={quiz?.type}
          >
            <option value="TASK">{t("forms.task")}</option>
            <option value="QUESTIONNAIRE">{t("forms.questionnaire")}</option>
          </Select>
        </div>
        <div>
          <FormLabel htmlFor="schedule">{t("forms.schedule")}</FormLabel>
          <Select
            id="schedule"
            {...register("schedule", {
              required: t("forms.validation.required"),
            })}
            defaultValue={quiz?.schedule}
          >
            <option value="BEFORE">{t("forms.before")}</option>
            <option value="AFTER">{t("forms.after")}</option>
          </Select>
        </div>
        <div>
          <input defaultValue={lessonId} hidden={true} name={"lessonId"} />
        </div>
      </FormControl>
      <Button
        mt={4}
        bg="primary.DEFAULT"
        color="white"
        isLoading={isPending}
        type="submit"
      >
        {t("common.buttons.save")}
      </Button>
    </form>
  );
}
